/* -----------------Globals--------------- */
import { useMemo } from "react";
import { List, Text } from "react-native-paper";

/* -----------------Child components--------------- */
import Box from "@memorang/ui/components/Box";

/* -----------------Hooks--------------- */
import { useAppTheme } from "@hooks/useAppTheme";

/* -----------------Helpers--------------- */
import type { StateDetails } from "@memorang/types";
import ChooseStateScreen from "@memorang/ui/components/ChooseStateScreen";
import type { EmptyViewProps } from "@memorang/ui/components/EmptyView";
import type { ListItem } from "@memorang/ui/components/ItemList";
import SearchNoResult from "../../../../assets/no-search-results.svg";
import type { NextButtonHandler } from "../types";

type Props = {
	selectedStateCode?: string;
	handleSelectState: (stateCode: string) => void;
	states: StateDetails[];
	handleSearchState?: (query: string) => void;
	searchQuery: string;
	handleNextAction: NextButtonHandler;
	cta?: string;
};

const StateAvatar = ({ abbr }: { abbr: string }) => {
	const theme = useAppTheme();
	return (
		<Box
			width={40}
			height={40}
			borderRadius={20}
			justifyContent="center"
			alignItems="center"
			style={{ backgroundColor: theme.colors.primaryContainer }}
		>
			<Text
				variant="titleSmall"
				style={{ color: theme.colors.onPrimaryContainer }}
			>
				{abbr}
			</Text>
		</Box>
	);
};

export const ChooseStateContainer = ({
	selectedStateCode,
	handleSelectState,
	states,
	handleSearchState,
	searchQuery,
	handleNextAction,
	cta,
}: Props) => {
	const theme = useAppTheme();

	const handleNext = () => {
		handleNextAction("chooseContext");
	};
	const selectedState = states.find(
		(state) => state.code === selectedStateCode,
	);

	const itemsWithIcons: ListItem[] = useMemo(
		() =>
			states.map((state) => ({
				id: state.code,
				title: state.name,
				customLeft: (p) => (
					<List.Icon {...p} icon={() => <StateAvatar abbr={state.code} />} />
				),
				customRight: () =>
					selectedStateCode === state.code ? (
						<List.Icon icon="check" color={theme.colors.primary} />
					) : null,
			})),
		[states, selectedStateCode, theme.colors.primary],
	);

	const emptyState: EmptyViewProps = {
		title: "No states found",
		subtitle: "Try adjusting your filters or search query",
		image: SearchNoResult,
		cta: "Clear search",
		handleAction: () => handleSearchState?.(""),
	};

	return (
		<ChooseStateScreen
			emptyState={emptyState}
			handleNext={handleNext}
			handleSelectState={handleSelectState}
			items={itemsWithIcons}
			handleSearchState={handleSearchState}
			searchQuery={searchQuery}
			selectedStateCode={selectedStateCode}
			selectedStateName={selectedState?.name}
			cta={cta}
		/>
	);
};
